import type { ReactNode } from "react";
import { Inbox } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/Button";
import { cn } from "@/lib/utils/cn";

export interface EliteEmptyStateProps {
  title: string;
  titleKey?: string;
  description: string;
  descriptionKey?: string;
  actionLabel?: string;
  actionLabelKey?: string;
  onAction?: () => void;
  icon?: ReactNode;
  className?: string;
}

export const EliteEmptyState = ({
  title,
  titleKey,
  description,
  descriptionKey,
  actionLabel,
  actionLabelKey,
  onAction,
  icon,
  className,
}: EliteEmptyStateProps): JSX.Element => {
  const { t } = useTranslation();
  const resolvedTitle = titleKey ? t(titleKey) : title;
  const resolvedDescription = descriptionKey ? t(descriptionKey) : description;
  const resolvedActionLabel = actionLabelKey ? t(actionLabelKey) : actionLabel;

  return (
    <div
      className={cn(
        "elite-empty-surface flex min-h-56 flex-col items-center justify-center rounded-[24px] border border-dashed p-8 text-center backdrop-blur-xl",
        className,
      )}
    >
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
        {icon ?? <Inbox className="h-7 w-7" aria-hidden="true" />}
      </div>
      <h2 className="text-lg font-semibold text-[hsl(var(--text))]">{resolvedTitle}</h2>
      <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">{resolvedDescription}</p>
      {resolvedActionLabel ? (
        <Button className="mt-5 rounded-[24px]" type="button" onClick={onAction}>
          {resolvedActionLabel}
        </Button>
      ) : null}
    </div>
  );
};
